import React from 'react';
import { CommonUtils } from './commonUtils';

// Types
export interface FormData {
  name: string;
  description: string;
  benefitType: 'stamp' | 'point' | 'both';
  stampConfig: {
    maxStamps: number;
    rewardType: string;
    rewardValue: number;
  };
  pointConfig: {
    earnRate: number;
    redeemRate: number;
    minRedeem: number;
  };
}

export interface FormErrors {
  name?: string;
  description?: string;
  benefitType?: string;
  stampConfig?: {
    maxStamps?: string;
    rewardType?: string;
    rewardValue?: string;
  };
  pointConfig?: {
    earnRate?: string;
    redeemRate?: string;
    minRedeem?: string;
  };
}

export interface MembershipStats {
  totalMembers: number;
  activeMembers: number;
  newMembersThisMonth: number;
  totalStamps: number;
  totalPoints: number;
  stampRedemptions: number;
  pointRedemptions: number;
}

// MembershipFormUtils Class
export class MembershipFormUtils {
  // 기본 폼 데이터 생성
  static getDefaultFormData(): FormData {
    return {
      name: '',
      description: '',
      benefitType: 'stamp',
      stampConfig: {
        maxStamps: 10,
        rewardType: 'discount',
        rewardValue: 0
      },
      pointConfig: {
        earnRate: 1,
        redeemRate: 100,
        minRedeem: 1000
      }
    };
  }

  // 폼 유효성 검사
  static validateForm(formData: FormData): FormErrors {
    const errors: FormErrors = {};

    if (!formData.name.trim()) {
      errors.name = '멤버십명을 입력해주세요.';
    } else if (formData.name.length > 50) {
      errors.name = '멤버십명은 50자 이하로 입력해주세요.';
    }

    if (!formData.description.trim()) {
      errors.description = '멤버십 설명을 입력해주세요.';
    } else if (formData.description.length > 200) {
      errors.description = '멤버십 설명은 200자 이하로 입력해주세요.';
    }

    if (!formData.benefitType) {
      errors.benefitType = '혜택 유형을 선택해주세요.';
    }

    // 스탬프 설정 검증
    if (formData.benefitType === 'stamp' || formData.benefitType === 'both') {
      if (!formData.stampConfig.maxStamps || formData.stampConfig.maxStamps < 1) {
        errors.stampConfig = {
          ...errors.stampConfig,
          maxStamps: '최대 스탬프 수는 1 이상이어야 합니다.'
        };
      }

      if (!formData.stampConfig.rewardType) {
        errors.stampConfig = {
          ...errors.stampConfig,
          rewardType: '보상 유형을 선택해주세요.'
        };
      }

      if (!formData.stampConfig.rewardValue || formData.stampConfig.rewardValue < 0) {
        errors.stampConfig = {
          ...errors.stampConfig,
          rewardValue: '보상 금액은 0 이상이어야 합니다.'
        };
      }
    }

    // 포인트 설정 검증
    if (formData.benefitType === 'point' || formData.benefitType === 'both') {
      if (!formData.pointConfig.earnRate || formData.pointConfig.earnRate < 0) {
        errors.pointConfig = {
          ...errors.pointConfig,
          earnRate: '적립률은 0 이상이어야 합니다.'
        };
      }

      if (!formData.pointConfig.redeemRate || formData.pointConfig.redeemRate < 0) {
        errors.pointConfig = {
          ...errors.pointConfig,
          redeemRate: '사용률은 0 이상이어야 합니다.'
        };
      }

      if (!formData.pointConfig.minRedeem || formData.pointConfig.minRedeem < 0) {
        errors.pointConfig = {
          ...errors.pointConfig,
          minRedeem: '최소 사용 포인트는 0 이상이어야 합니다.'
        };
      }
    }

    return errors;
  }

  // 입력 변경 처리
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value, type } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));

    // 해당 필드의 에러 제거
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setErrors(prev => {
        const parentKey = parent as keyof FormErrors;
        const parentValue = prev[parentKey];
        return {
          ...prev,
          [parent]: {
            ...(parentValue && typeof parentValue === 'object' ? parentValue : {}),
            [child]: undefined
          }
        };
      });
    } else {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }

  // 프로젝트 정보 로드
  static loadProjectDetail(): any {
    return CommonUtils.loadProjectDetail();
  }

  // 멤버십 상태 배지 생성
  static getStatusBadge(status: string) {
    return CommonUtils.getStatusBadge(status, 'membership');
  }

  // 멤버십 혜택 유형 정보 가져오기
  static getBenefitTypeInfo(type: string) {
    const typeMap: { [key: string]: { label: string; color: string; icon: string } } = {
      'stamp': { label: '스탬프', color: 'bg-blue-100 text-blue-800', icon: 'Stamp' },
      'point': { label: '포인트', color: 'bg-green-100 text-green-800', icon: 'Coins' },
      'both': { label: '스탬프+포인트', color: 'bg-purple-100 text-purple-800', icon: 'Award' }
    };
    return typeMap[type] || { label: '알 수 없음', color: 'bg-gray-100 text-gray-800', icon: 'Gift' };
  }

  // 뷰 타입 변경 처리
  static handleViewTypeChange(
    viewType: 'list' | 'grid',
    setViewType: React.Dispatch<React.SetStateAction<'list' | 'grid'>>
  ) {
    setViewType(viewType);
    localStorage.setItem('membershipViewType', viewType);
  }

  // 멤버십 생성 페이지로 이동
  static handleMembershipCreate(navigate: (path: string) => void) {
    navigate('/memberships/create');
  }

  // 멤버십 수정 페이지로 이동
  static handleMembershipEdit(navigate: (path: string) => void, membershipCode: string, currentPage: number, pageSize: number) {
    navigate(`/memberships/${encodeURIComponent(membershipCode)}/update?page=${currentPage}&size=${pageSize}`);
  }

  // 멤버십 삭제 처리
  static handleMembershipDelete(membershipCode: string, onDelete?: (code: string) => void) {
    return CommonUtils.handleDelete(membershipCode, '멤버십', onDelete);
  }

  // 폼 제출 처리 (멤버십 생성)
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getProjectCode: () => string,
    createMembership: (projectCode: string, data: any) => Promise<any>,
    navigate: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const membershipData = {
        name: formData.name,
        description: formData.description,
        benefitType: formData.benefitType,
        stampConfig: formData.stampConfig,
        pointConfig: formData.pointConfig
      };

      const result = await createMembership(projectCode, membershipData);
      
      if (result.success) {
        navigate('/memberships');
      } else {
        setSubmitError(result.message || '멤버십 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('멤버십 생성 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '멤버십 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 폼 제출 처리 (멤버십 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getProjectCode: () => string,
    membershipCode: string,
    updateMembership: (projectCode: string, membershipCode: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      
      // rewardType 결정 (서버에 보낼 값)
      const rewardType = formData.benefitType === 'stamp' ? 'STAMP' : 
                        formData.benefitType === 'point' ? 'POINT' : 'BOTH';
      
      // 멤버십 데이터 구성 (서버가 기대하는 필드명으로 매핑)
      const membershipData: any = {
        title: formData.name,
        status: 'ACTIVE', // 수정 페이지에서 상태 관리 필요시 추가
        description: formData.description || '',
        rewardType: rewardType,
        rewardPolicyOwner: 'HEADQUARTERS', // 수정 페이지에서 관리 필요시 추가
        paidMembership: 0, // 수정 페이지에서 관리 필요시 추가
        membershipLevel: 0, // 수정 페이지에서 관리 필요시 추가
        stampConfig: {
          maxStamps: formData.stampConfig.maxStamps,
          rewardType: formData.stampConfig.rewardType,
          rewardValue: formData.stampConfig.rewardValue
        },
        pointConfig: {
          earnRate: formData.pointConfig.earnRate,
          redeemRate: formData.pointConfig.redeemRate || 100,
          minRedeem: formData.pointConfig.minRedeem || 1000
        }
      };

      const result = await updateMembership(projectCode, membershipCode, membershipData);
      
      if (result.success) {
        setHasChanges(false);
        setOriginalData({...formData});
        // 멤버십 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/membership?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '멤버십 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('멤버십 수정 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '멤버십 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 모바일 사이드바 리사이즈 처리
  static useMobileSidebarResize(setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>) {
    React.useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          setIsMobileSidebarOpen(false);
        }
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [setIsMobileSidebarOpen]);
  }
}